import { SOCIAL_SERVICE_KEYS, PLATFORM_SERVICE_KEYS } from './serviceKeys';

// Service identifiers match the API `service` values AND the icon keys used by
// <SocialIcon> / <PlatformIcon>, so a service string can be passed straight to
// either icon component. Keep in sync with the catalogs below.
export type SocialService = (typeof SOCIAL_SERVICE_KEYS)[number];
export type PlatformService = (typeof PLATFORM_SERVICE_KEYS)[number];
export type LinkService = SocialService | PlatformService;
export type LinkKind = 'social' | 'platform';

export interface ServiceMeta {
  service: LinkService;
  name: string;
  color: string;
  placeholder: string;
  // Fixed domain (incl. any literal "@") a handle slots into, e.g. "https://tiktok.com/@".
  // Present only for services whose profile URL is a deterministic domain + handle —
  // website/substack allow custom domains, so those stay full-URL entry.
  handlePrefix?: string;
}

export const SOCIAL_CATALOG: ServiceMeta[] = [
  {
    service: 'website',
    name: 'Website',
    color: '#64748B',
    placeholder: 'https://yoursite.com',
  },
  {
    service: 'instagram',
    name: 'Instagram',
    color: '#E1306C',
    placeholder: 'yourhandle',
    handlePrefix: 'https://instagram.com/',
  },
  {
    service: 'x',
    name: 'X',
    color: '#000000',
    placeholder: 'yourhandle',
    handlePrefix: 'https://x.com/',
  },
  {
    service: 'tiktok',
    name: 'TikTok',
    color: '#000000',
    placeholder: 'yourhandle',
    handlePrefix: 'https://tiktok.com/@',
  },
  {
    service: 'threads',
    name: 'Threads',
    color: '#000000',
    placeholder: 'yourhandle',
    handlePrefix: 'https://threads.net/@',
  },
  {
    service: 'facebook',
    name: 'Facebook',
    color: '#1877F2',
    placeholder: 'yourpage',
    handlePrefix: 'https://facebook.com/',
  },
  {
    service: 'substack',
    name: 'Substack',
    color: '#FF6719',
    placeholder: 'https://yourname.substack.com',
  },
  {
    service: 'medium',
    name: 'Medium',
    color: '#000000',
    placeholder: 'yourhandle',
    handlePrefix: 'https://medium.com/@',
  },
  {
    service: 'linkedin',
    name: 'LinkedIn',
    color: '#0A66C2',
    placeholder: 'yourhandle',
    handlePrefix: 'https://linkedin.com/in/',
  },
  {
    service: 'reddit',
    name: 'Reddit',
    color: '#FF4500',
    placeholder: 'yourhandle',
    handlePrefix: 'https://reddit.com/u/',
  },
  {
    service: 'snapchat',
    name: 'Snapchat',
    color: '#F5C800',
    placeholder: 'yourhandle',
    handlePrefix: 'https://snapchat.com/add/',
  },
  {
    service: 'pinterest',
    name: 'Pinterest',
    color: '#E60023',
    placeholder: 'yourhandle',
    handlePrefix: 'https://pinterest.com/',
  },
  {
    service: 'github',
    name: 'GitHub',
    color: '#181717',
    placeholder: 'yourhandle',
    handlePrefix: 'https://github.com/',
  },
  {
    service: 'dribbble',
    name: 'Dribbble',
    color: '#EA4C89',
    placeholder: 'yourhandle',
    handlePrefix: 'https://dribbble.com/',
  },
  {
    service: 'behance',
    name: 'Behance',
    color: '#1769FF',
    placeholder: 'yourhandle',
    handlePrefix: 'https://www.behance.net/',
  },
  {
    service: 'goodreads',
    name: 'Goodreads',
    color: '#1E1914',
    placeholder: 'https://goodreads.com/author/show/...',
  },
];

export const PLATFORM_CATALOG: ServiceMeta[] = [
  {
    service: 'spotify',
    name: 'Spotify',
    color: '#1DB954',
    placeholder: 'https://open.spotify.com/artist/...',
  },
  {
    service: 'apple-music',
    name: 'Apple Music',
    color: '#FA243C',
    placeholder: 'https://music.apple.com/artist/...',
  },
  {
    service: 'youtube',
    name: 'YouTube',
    color: '#FF0000',
    placeholder: 'https://youtube.com/@yourchannel',
  },
  {
    service: 'youtube-music',
    name: 'YouTube Music',
    color: '#FF0000',
    placeholder: 'https://music.youtube.com/...',
  },
  {
    service: 'audiomack',
    name: 'Audiomack',
    color: '#FFA200',
    placeholder: 'https://audiomack.com/yourhandle',
  },
  {
    service: 'soundcloud',
    name: 'SoundCloud',
    color: '#FF5500',
    placeholder: 'https://soundcloud.com/yourhandle',
  },
  {
    service: 'twitch',
    name: 'Twitch',
    color: '#9146FF',
    placeholder: 'yourchannel',
    handlePrefix: 'https://twitch.tv/',
  },
];

// Where an online event can be hosted. Stored directly in the event's `venue`
// column (display name, no service key) and validated server-side against the
// same list (schema.ts EVENT_PLATFORMS) — keep the two in sync. Separate from
// the link catalogs above: those are profile links, this is event hosting.
export const EVENT_PLATFORMS = [
  'Twitch',
  'YouTube Live',
  'Instagram Live',
  'TikTok Live',
  'X (Spaces)',
  'Facebook Live',
  'Zoom',
  'Google Meet',
  'Discord',
] as const;
export type EventPlatform = (typeof EVENT_PLATFORMS)[number];

const META_BY_SERVICE = new Map<LinkService, ServiceMeta>(
  [...SOCIAL_CATALOG, ...PLATFORM_CATALOG].map((m) => [m.service, m]),
);

export const serviceMeta = (service: LinkService): ServiceMeta | undefined =>
  META_BY_SERVICE.get(service);

// Handle -> full URL, for services with a fixed domain. Strips any leading "@"
// the user typed, since handlePrefix already includes it where the platform needs it.
export const buildHandleUrl = (meta: ServiceMeta, handle: string): string =>
  `${meta.handlePrefix ?? ''}${handle.trim().replace(/^@+/, '')}`;

// Reverse of buildHandleUrl, for populating the handle field when editing an
// existing link. Falls back to the raw url if it doesn't match the expected prefix.
export const extractHandle = (meta: ServiceMeta, url: string): string =>
  meta.handlePrefix && url.startsWith(meta.handlePrefix)
    ? url.slice(meta.handlePrefix.length)
    : url;

const PLATFORM_SERVICES = new Set<LinkService>(
  PLATFORM_CATALOG.map((p) => p.service),
);

// Mirrors the server's linkKindForService — kept in sync with the catalogs.
export const linkKindForService = (service: LinkService): LinkKind =>
  PLATFORM_SERVICES.has(service) ? 'platform' : 'social';
